import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditCard, Server, Cloud, DollarSign, FileText, Download, ExternalLink, Calendar, Coins, Wallet, ArrowUpRight, ArrowDownRight, Plus } from 'lucide-react';
import { showError, showSuccess } from '@/lib/toast';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

export function Billing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [customAmount, setCustomAmount] = useState('25');

  const { data: billing } = useQuery({
    queryKey: ['instance-billing'],
    queryFn: async () => {
      const { data } = await api.get('/billing/instances');
      return data;
    },
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: async () => {
      const { data } = await api.get('/billing/payment-methods');
      return data;
    },
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const { data } = await api.get('/billing/invoices');
      return data;
    },
  });

  const { data: walletBalance } = useQuery({
    queryKey: ['wallet-balance'],
    queryFn: async () => {
      const { data } = await api.get('/wallet/balance');
      return data;
    },
  });

  const { data: walletTransactions } = useQuery({
    queryKey: ['wallet-transactions'],
    queryFn: async () => {
      const { data} = await api.get('/wallet/transactions?limit=10');
      return data;
    },
  });

  const handleAddFunds = async (amountDollars?: number) => {
    const amount = amountDollars || parseFloat(customAmount);
    if (!amount || amount < 5) {
      showError('Minimum amount is $5');
      return;
    }

    try {
      const amountCents = Math.round(amount * 100);
      const { data } = await api.post('/billing/credit-checkout', { amountCents });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Error');
    }
  };

  const handleManage = async () => {
    try {
      const { data } = await api.post('/billing/portal', {});
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Error');
    }
  };

  const totalMonthly = billing?.totalMonthlyCents
    ? `$${(billing.totalMonthlyCents / 100).toFixed(0)}`
    : '$0';

  const queryClient = useQueryClient();
  const fulfilledRef = useRef(false);

  // After Stripe redirect: fulfill the session to ensure instance is created
  // This handles cases where the webhook was missed or delayed
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const isSuccess = urlParams.get('success') === 'true';

    if (isSuccess && sessionId && !fulfilledRef.current) {
      fulfilledRef.current = true;
      api.post('/billing/fulfill-session', { sessionId })
        .then(({ data }) => {
          if (data.instance) {
            showSuccess(`Instance "${data.instance.name}" is being deployed!`);
            queryClient.invalidateQueries({ queryKey: ['instance-billing'] });
            // Navigate to instance details and clean URL
            navigate(`/dashboard/instances/${data.instance.id}`, { replace: true });
          } else {
            showSuccess(t('billing.paymentSuccess'));
            window.history.replaceState({}, '', '/dashboard/billing');
          }
        })
        .catch(() => {
          showSuccess(t('billing.paymentSuccess'));
          window.history.replaceState({}, '', '/dashboard/billing');
        });
    }
  }, []);

  return (
    <div className="max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('billing.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('billing.subtitle')}
          </p>
        </div>
        <Button onClick={handleManage} className="gap-2 w-full sm:w-auto">
          <CreditCard className="h-4 w-4" />
          {t('billing.manageSubscriptions')}
        </Button>
      </div>

      <Tabs defaultValue="subscriptions" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="subscriptions">
            {t('billing.subscriptionsTab')}
          </TabsTrigger>
          <TabsTrigger value="wallet" className="gap-1.5">
            <Coins className="h-3.5 w-3.5" />
            Wallet
          </TabsTrigger>
          <TabsTrigger value="payment" className="gap-1.5">
            <CreditCard className="h-3.5 w-3.5" />
            Payment
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Invoices
          </TabsTrigger>
        </TabsList>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="space-y-6">
          {/* Total Spend Card */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl gradient-primary flex items-center justify-center">
                    <DollarSign className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('billing.totalMonthlySpend')}
                    </p>
                    <p className="text-3xl font-bold mt-1">
                      {totalMonthly}
                      <span className="text-lg font-normal text-muted-foreground">/mo</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-2xl bg-emerald-500 flex items-center justify-center">
                      <Server className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Active Instances
                      </p>
                      <p className="text-3xl font-bold mt-1">
                        {billing?.instances?.filter((i: any) => i.status === 'running')?.length || 0}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/dashboard/new')} size="sm" className="gap-2">
                    {t('billing.newInstance')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Instance Subscriptions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{t('billing.activeSubscriptions')}</CardTitle>
              <CardDescription>
                Manage your active OpenClaw instances and subscriptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!billing?.instances?.length ? (
                <div className="text-center py-12">
                  <div className="h-16 w-16 rounded-full bg-muted mx-auto flex items-center justify-center mb-4">
                    <Server className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">No active subscriptions</h3>
                  <p className="text-muted-foreground mb-6">
                    {t('billing.noSubscriptions')}
                  </p>
                  <Button onClick={() => navigate('/dashboard/new')}>
                    {t('billing.deployFirst')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {billing.instances.map((instance: any) => {
                    const isManaged = instance.hosting_type === 'managed';
                    const isPlatformKey = instance.key_mode === 'platform_key';
                    const price = isPlatformKey ? '$5' : isManaged ? '$10' : '$5';
                    return (
                      <div
                        key={instance.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/dashboard/instances/${instance.id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            'p-3 rounded-xl',
                            isPlatformKey ? 'bg-emerald-500/10' : isManaged ? 'bg-primary/10' : 'bg-orange-500/10'
                          )}>
                            {isPlatformKey
                              ? <Coins className="h-6 w-6 text-emerald-500" />
                              : isManaged
                                ? <Cloud className="h-6 w-6 text-primary" />
                                : <Server className="h-6 w-6 text-orange-500" />}
                          </div>
                          <div>
                            <p className="font-semibold text-base">{instance.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">
                                {isPlatformKey ? 'Platform Key' : isManaged ? 'Managed' : 'BYOS'}
                              </Badge>
                              <Badge
                                variant={
                                  instance.status === 'running' ? 'success' :
                                  instance.status === 'paused' ? 'warning' :
                                  instance.status === 'error' ? 'destructive' :
                                  'secondary'
                                }
                                className="text-xs capitalize"
                              >
                                {instance.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                Since {new Date(instance.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-primary">
                            {price}<span className="text-sm font-normal">/mo</span>
                          </p>
                          {isPlatformKey && (
                            <p className="text-xs text-muted-foreground">+ wallet usage</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Wallet Tab */}
        <TabsContent value="wallet" className="space-y-6">
          {/* Wallet Balance Card */}
          <Card className="border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-2xl bg-emerald-500 flex items-center justify-center">
                    <Wallet className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Wallet Balance
                    </p>
                    <p className="text-4xl font-bold mt-1">
                      ${((walletBalance?.balanceCents || 0) / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Total used: ${((walletBalance?.totalUsedCents || 0) / 100).toFixed(2)}
                    </p>
                  </div>
                </div>
                <Button className="gap-2" size="lg">
                  <Plus className="h-4 w-4" />
                  Add Funds
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Add Funds */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Add Funds</CardTitle>
              <CardDescription>
                Enter any amount (minimum $5) to add to your wallet
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Amount Slider */}
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-5xl font-bold text-emerald-600">
                    ${customAmount}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Use slider or buttons to select amount
                  </p>
                </div>

                {/* Slider */}
                <div className="space-y-4">
                  <input
                    type="range"
                    min="5"
                    max="500"
                    step="5"
                    value={customAmount}
                    onChange={(e: any) => setCustomAmount(e.target.value)}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 slider"
                    style={{
                      background: `linear-gradient(to right, rgb(16 185 129) 0%, rgb(16 185 129) ${((parseFloat(customAmount) - 5) / 495) * 100}%, rgb(229 231 235) ${((parseFloat(customAmount) - 5) / 495) * 100}%, rgb(229 231 235) 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>$5</span>
                    <span>$500</span>
                  </div>
                </div>

                {/* Quick Amount Buttons */}
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {[10, 25, 50, 100, 200, 300, 500].map((amount) => (
                    <Button
                      key={amount}
                      variant={customAmount === String(amount) ? "default" : "outline"}
                      onClick={() => setCustomAmount(String(amount))}
                      size="sm"
                    >
                      ${amount}
                    </Button>
                  ))}
                </div>

                {/* Add Funds Button */}
                <Button
                  onClick={() => handleAddFunds()}
                  size="lg"
                  className="w-full gap-2"
                  disabled={!customAmount || parseFloat(customAmount) < 5}
                >
                  <Plus className="h-5 w-5" />
                  Add ${customAmount} to Wallet
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Minimum: $5 • Increment: $5 • Your wallet balance can be used for all services
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Transaction History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Recent Transactions</CardTitle>
              <CardDescription>
                Your wallet transaction history
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!walletTransactions?.transactions?.length ? (
                <div className="text-center py-12">
                  <div className="h-16 w-16 rounded-full bg-muted mx-auto flex items-center justify-center mb-4">
                    <Wallet className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">No transactions yet</h3>
                  <p className="text-muted-foreground">
                    Your wallet transactions will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {walletTransactions.transactions.map((tx: any) => {
                    const isDebit = tx.amountCents < 0;
                    const absAmount = Math.abs(tx.amountCents);
                    return (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card"
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            'p-3 rounded-xl',
                            isDebit ? 'bg-red-500/10' : 'bg-emerald-500/10'
                          )}>
                            {isDebit ? (
                              <ArrowDownRight className="h-6 w-6 text-red-500" />
                            ) : (
                              <ArrowUpRight className="h-6 w-6 text-emerald-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">
                              {tx.description || tx.type}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(tx.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={cn(
                            "font-bold text-lg",
                            isDebit ? "text-red-600" : "text-emerald-600"
                          )}>
                            {isDebit ? '-' : '+'}${(absAmount / 100).toFixed(2)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Balance: ${(tx.balanceAfter / 100).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Methods Tab */}
        <TabsContent value="payment" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl">Payment Methods</CardTitle>
                  <CardDescription>
                    Manage your payment methods and billing information
                  </CardDescription>
                </div>
                <Button onClick={handleManage} variant="outline" size="sm" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  Manage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!paymentMethods?.paymentMethods?.length ? (
                <div className="text-center py-12">
                  <div className="h-16 w-16 rounded-full bg-muted mx-auto flex items-center justify-center mb-4">
                    <CreditCard className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">No payment methods</h3>
                  <p className="text-muted-foreground mb-6">
                    Add a payment method to start using OpenClaw services
                  </p>
                  <Button onClick={handleManage}>
                    Add Payment Method
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentMethods.paymentMethods.map((pm: any) => (
                    <div
                      key={pm.id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-primary/10">
                          <CreditCard className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold capitalize">
                            {pm.brand} •••• {pm.last4}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Expires {pm.expMonth}/{pm.expYear}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary">Default</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Billing History</CardTitle>
              <CardDescription>
                View and download your invoices and payment history
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!invoicesData?.invoices?.length ? (
                <div className="text-center py-12">
                  <div className="h-16 w-16 rounded-full bg-muted mx-auto flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">No invoices yet</h3>
                  <p className="text-muted-foreground">
                    Your payment history will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {invoicesData.invoices.map((invoice: any) => {
                    const amountPaid = parseInt(invoice.amountPaid) || 0;
                    const created = invoice.created ? new Date(invoice.created * 1000) : null;
                    const isValidDate = created && !isNaN(created.getTime());

                    return (
                      <div
                        key={invoice.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            'p-3 rounded-xl',
                            invoice.status === 'paid' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                          )}>
                            <FileText className={cn(
                              'h-6 w-6',
                              invoice.status === 'paid' ? 'text-emerald-500' : 'text-amber-500'
                            )} />
                          </div>
                          <div>
                            <p className="font-semibold">
                              {invoice.number || `Invoice #${invoice.id.slice(-8)}`}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge
                                variant={invoice.status === 'paid' ? 'success' : 'warning'}
                                className="text-xs capitalize"
                              >
                                {invoice.status}
                              </Badge>
                              {isValidDate && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {created.toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {invoice.description || 'Subscription'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-bold text-lg">
                              ${(amountPaid / 100).toFixed(2)}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase">
                              {invoice.currency || 'usd'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {invoice.hostedUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(invoice.hostedUrl, '_blank')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                            {invoice.pdfUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(invoice.pdfUrl, '_blank')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
